import { useCallback, useEffect, useMemo, useState } from 'react';
import type { QuickFillItem } from '../../../shared/types';
import { perf } from '../../../shared/lib/perfLogger';
import {
  buildFilteredRowPositionMap,
  resolveQuickFillNavigationState,
  type QuickFillSelectedCell,
  type ViewportRange,
} from '../lib/quickFillNavigationModel';
import {
  areRowIndicesEqual,
  buildNavigationMarkers,
  buildNavigatorRowCoverage,
  toggleRowIndex,
} from '../ui/quickFillTourState';
import type { QuickFillNavigationMarker } from '../ui/components/QuickFillNavigationDock';

export interface NavigationRequest {
  rowIndex: number;
  requestId: number;
  columnLabel?: string;
}

export interface UseQuickFillNavigationOptions {
  quickFillData: QuickFillItem[];
  selectedCells: QuickFillSelectedCell[];
  selectedRows: number[];
  visiblePropertyLabels: string[];
  initialCollapsed?: boolean;
}

export interface UseQuickFillNavigationResult {
  navigationDockCollapsed: boolean;
  setNavigationDockCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  navigationMarkerRows: number[];
  setNavigationMarkerRows: React.Dispatch<React.SetStateAction<number[]>>;
  filteredRowIndices: number[];
  hasReportedFilteredRows: boolean;
  viewportRange: ViewportRange;
  navigationRequest: NavigationRequest | null;
  setNavigationRequest: React.Dispatch<React.SetStateAction<NavigationRequest | null>>;
  // Single source of derived navigation state — consumers must not recompute it.
  navigationState: ReturnType<typeof resolveQuickFillNavigationState>;
  filteredRowPositionMap: ReturnType<typeof buildFilteredRowPositionMap>;
  activeNavigationRowIndex: number | null;
  activeNavigationPosition: number | null;
  currentNavigationPosition: number | null;
  activeNavigationRowLabel: string | null;
  activeRowMarked: boolean;
  canJumpToActive: boolean;
  canToggleMarker: boolean;
  markerItems: QuickFillNavigationMarker[];
  navigatorRowCoverage: number[];
  handleFilteredRowIndicesChange: (rowIndices: number[]) => void;
  handleViewportRangeChange: (range: ViewportRange) => void;
  requestRowNavigation: (rowIndex: number) => void;
  handleJumpToPosition: (position: number) => void;
  handleToggleActiveMarker: () => void;
  handleRemoveMarker: (rowIndex: number) => void;
  handleJumpToActiveLocation: () => void;
}

export const useQuickFillNavigation = ({
  quickFillData,
  selectedCells,
  selectedRows,
  visiblePropertyLabels,
  initialCollapsed = false,
}: UseQuickFillNavigationOptions): UseQuickFillNavigationResult => {
  const [navigationDockCollapsed, setNavigationDockCollapsed] = useState(initialCollapsed);
  const [navigationMarkerRows, setNavigationMarkerRows] = useState<number[]>([]);
  const [filteredRowIndices, setFilteredRowIndices] = useState<number[]>([]);
  const [hasReportedFilteredRows, setHasReportedFilteredRows] = useState(false);
  const [viewportRange, setViewportRange] = useState<ViewportRange>({
    startPosition: null,
    endPosition: null,
  });
  const [navigationRequest, setNavigationRequest] = useState<NavigationRequest | null>(null);

  useEffect(() => {
    if (initialCollapsed) {
      setNavigationDockCollapsed(true);
    }
  }, [initialCollapsed]);

  useEffect(() => {
    setNavigationMarkerRows((previous) =>
      previous.filter((rowIndex) => rowIndex >= 0 && rowIndex < quickFillData.length)
    );
  }, [quickFillData.length]);

  const filteredRowPositionMap = useMemo(
    () => buildFilteredRowPositionMap(filteredRowIndices),
    [filteredRowIndices]
  );

  const navigationState = useMemo(
    () =>
      resolveQuickFillNavigationState({
        selectedCells,
        selectedRows,
        navigationRequest,
        quickFillData,
        filteredRowPositionMap,
        navigationMarkerRows,
        viewportRange,
      }),
    [
      filteredRowPositionMap,
      navigationMarkerRows,
      navigationRequest,
      quickFillData,
      selectedCells,
      selectedRows,
      viewportRange,
    ]
  );

  const {
    activeNavigationRowIndex,
    activeNavigationPosition,
    currentNavigationPosition,
    activeNavigationRowLabel,
    activeRowMarked,
    canJumpToActive,
    canToggleMarker,
  } = navigationState;

  const markerItems = useMemo<QuickFillNavigationMarker[]>(
    () => buildNavigationMarkers(navigationMarkerRows, quickFillData, filteredRowPositionMap),
    [filteredRowPositionMap, navigationMarkerRows, quickFillData]
  );

  const navigatorRowCoverage = useMemo(
    () => buildNavigatorRowCoverage(filteredRowIndices, quickFillData, visiblePropertyLabels),
    [filteredRowIndices, quickFillData, visiblePropertyLabels]
  );

  const handleFilteredRowIndicesChange = useCallback(
    (rowIndices: number[]) => {
      perf.log('QF-WizardFilter', 'table reported visible row indices', {
        rowCount: rowIndices.length,
        sampleRows: rowIndices
          .slice(0, 5)
          .map((rowIndex) => quickFillData[rowIndex]?.Name || `Row ${rowIndex + 1}`),
      });
      setHasReportedFilteredRows(true);
      setFilteredRowIndices((previous) => (areRowIndicesEqual(previous, rowIndices) ? previous : rowIndices));
    },
    [quickFillData]
  );

  const handleViewportRangeChange = useCallback(
    ({ startPosition, endPosition }: ViewportRange) => {
      setViewportRange((previous) =>
        previous.startPosition === startPosition && previous.endPosition === endPosition
          ? previous
          : { startPosition, endPosition }
      );
    },
    []
  );

  const requestRowNavigation = useCallback((rowIndex: number) => {
    setNavigationRequest((previous) => ({
      rowIndex,
      requestId: (previous?.requestId ?? 0) + 1,
    }));
  }, []);

  const handleJumpToPosition = useCallback(
    (position: number) => {
      const targetRowIndex = filteredRowIndices[position];
      if (targetRowIndex == null) {
        return;
      }
      requestRowNavigation(targetRowIndex);
    },
    [filteredRowIndices, requestRowNavigation]
  );

  const handleToggleActiveMarker = useCallback(() => {
    if (activeNavigationRowIndex == null) {
      return;
    }

    setNavigationMarkerRows((previous) => toggleRowIndex(previous, activeNavigationRowIndex));
  }, [activeNavigationRowIndex]);

  const handleRemoveMarker = useCallback((rowIndex: number) => {
    setNavigationMarkerRows((previous) => previous.filter((existing) => existing !== rowIndex));
  }, []);

  const handleJumpToActiveLocation = useCallback(() => {
    if (activeNavigationPosition == null || activeNavigationPosition < 0) {
      return;
    }

    handleJumpToPosition(activeNavigationPosition);
  }, [activeNavigationPosition, handleJumpToPosition]);

  return useMemo(
    () => ({
      navigationDockCollapsed,
      setNavigationDockCollapsed,
      navigationMarkerRows,
      setNavigationMarkerRows,
      filteredRowIndices,
      hasReportedFilteredRows,
      viewportRange,
      navigationRequest,
      setNavigationRequest,
      navigationState,
      filteredRowPositionMap,
      activeNavigationRowIndex,
      activeNavigationPosition,
      currentNavigationPosition,
      activeNavigationRowLabel,
      activeRowMarked,
      canJumpToActive,
      canToggleMarker,
      markerItems,
      navigatorRowCoverage,
      handleFilteredRowIndicesChange,
      handleViewportRangeChange,
      requestRowNavigation,
      handleJumpToPosition,
      handleToggleActiveMarker,
      handleRemoveMarker,
      handleJumpToActiveLocation,
    }),
    [
      activeNavigationPosition,
      activeNavigationRowIndex,
      activeNavigationRowLabel,
      activeRowMarked,
      canJumpToActive,
      canToggleMarker,
      currentNavigationPosition,
      filteredRowIndices,
      handleFilteredRowIndicesChange,
      handleJumpToActiveLocation,
      handleJumpToPosition,
      handleRemoveMarker,
      handleToggleActiveMarker,
      handleViewportRangeChange,
      hasReportedFilteredRows,
      markerItems,
      navigationDockCollapsed,
      navigationMarkerRows,
      navigationRequest,
      navigationState,
      filteredRowPositionMap,
      navigatorRowCoverage,
      requestRowNavigation,
      setNavigationDockCollapsed,
      setNavigationMarkerRows,
      setNavigationRequest,
      viewportRange,
    ]
  );
};
